import classNames from "classnames";
import React from "react";
import PropTypes from "prop-types";

import ProductImage from "../shared/product-image";
import ActionLogger from "action-logger";
import ProductImageRotation from "helpers/product-image-rotation";
import MaternityHelper from "helpers/maternity-helper";
import {
  bindTouchGesturesWithAsyncHammer,
  resetPinchPanZoom,
  setupPinchPanZoom,
} from "helpers/touch-interaction-helper";
import {
  getProductImageAltText,
  getProductImageUrlsByOrientation,
  ProductImageSize,
} from "../../../helpers/product-image-helpers";
import { onEnterPress } from "../../../helpers/a11y-helper";
import { productPropType } from "../../propTypes";
import { FetchPriority } from "rtr-constants";

export class NarrowPDPImagery extends React.Component {
  constructor(props) {
    super(props);

    this.imageRef = React.createRef();
  }
  static propTypes = {
    isZoomModal: PropTypes.bool,
    product: productPropType,
    startingIndex: PropTypes.any, // not sure
    userData: PropTypes.object.isRequired,
    workingFilters: PropTypes.object,
  };

  componentDidMount() {
    bindTouchGesturesWithAsyncHammer(Hammer => {
      this.hammer = new Hammer(this.imageRef.current);

      this.hammer.on("swipeleft", () => {
        this.offsetPage(1);
      });

      this.hammer.on("swiperight", () => {
        this.offsetPage(-1);
      });

      if (this.props.isZoomModal) {
        this.logZoomModalShown();
        setupPinchPanZoom(this.hammer, this.imageRef.current, this);
      }
    });
  }

  componentWillUnmount() {
    if (this.hammer) {
      this.hammer.off("swipeleft swiperight")?.destroy();
    }
  }

  componentDidUpdate(oldProps) {
    if (this.props.workingFilters !== oldProps.workingFilters) {
      // Updating the displayed image when the workingFilters are updated. Onload,
      // it seems like the workingFilters are not present.
      this.setState({
        index: Object.keys(this.getFilteredImages() || {})[0],
      });
    }
  }

  logZoomModalShown = () => {
    ActionLogger.logAction({
      object_type: "node",
      action: "image_zoom",
      image_index: this.state.index,
    });
  };

  logSwipe = () => {
    if (this.state.loggedSwipe) {
      return;
    }

    ActionLogger.logAction({
      object_type: "node",
      action: "product_image_swipe",
      style: this.props.product.id,
    });

    this.setState({ loggedSwipe: true });
  };

  setPage = index => {
    this.setState({ index: index });
  };

  offsetPage = direction => {
    const keys = Object.keys(this.getFilteredImages() || {});
    const index = keys.indexOf(this.state.index);
    let newIndex = index + direction;

    this.logSwipe();

    if (index === -1) {
      // What happened? Just bail.
      return;
    }

    if (!keys[newIndex]) {
      // We're at the end of the list, let's wrap around
      if (newIndex < 0) {
        newIndex = keys.length - 1;
      } else {
        newIndex = 0;
      }
    }

    this.setState({ index: keys[newIndex] });
    if (this.props.isZoomModal) {
      resetPinchPanZoom(this.imageRef.current);
    }
  };

  getProductImageRotation = () => {
    const { product, userData } = this.props;
    const sizeFilters = this.props?.workingFilters?.canonicalSizes ?? [];
    const showMaternityBumpImage = MaternityHelper.showMaternityBumpImage(userData);

    const imageRotation = ProductImageRotation(sizeFilters, product, showMaternityBumpImage);
    return imageRotation;
  };

  getFilteredImages = () => {
    const { product } = this.props;
    const filteredImages = {};
    for (const orientation of this.getProductImageRotation()) {
      const image = product?.images?.[orientation];
      if (image) filteredImages[orientation] = image;
    }
    return filteredImages;
  };

  renderPagination = () => {
    const imageOrientations = Object.keys(this.getFilteredImages() || {});

    return (
      <div className="narrow-pdp-imagery__page-container" role="tablist">
        {imageOrientations.map((imageOrientation, index) => {
          const isCurrent = this.state.index === imageOrientation;

          return (
            <span
              aria-controls={`product-image-orientation-${imageOrientation}`}
              aria-selected={isCurrent}
              className={classNames("narrow-pdp-imagery__page", { "narrow-pdp-imagery__page--current": isCurrent })}
              key={imageOrientation}
              onClick={() => this.setPage(imageOrientation)}
              onKeyPress={onEnterPress(() => this.setPage(imageOrientation))}
              role="tab"
              tabIndex={index}>
              {imageOrientation}
            </span>
          );
        })}
      </div>
    );
  };

  state = {
    index: this.props.startingIndex || Object.keys(this.getFilteredImages() || {})[0],
    loggedSwipe: false,
  };

  render() {
    const { isZoomModal, product } = this.props;
    const images = this.getFilteredImages() || {};
    const imageOrientation = this.state?.index ?? null;

    return (
      <div
        className={classNames("narrow-pdp-imagery", {
          "zoom-imagery": isZoomModal,
        })}>
        <div
          className="narrow-pdp-imagery__image"
          ref={this.imageRef}
          role="tabpanel"
          aria-labelledby={`product-image-orientation-${imageOrientation}`}>
          <ProductImage
            altText={getProductImageAltText(product?.displayName, product?.designer?.displayName)}
            imageSize={isZoomModal ? ProductImageSize.x1080 : ProductImageSize.x270}
            imageGroup={getProductImageUrlsByOrientation(images, [imageOrientation])}
            fetchPriority={FetchPriority.HIGH}
          />
        </div>
        {this.renderPagination()}
      </div>
    );
  }
}

export default NarrowPDPImagery;
